/**
 * Flight identity — "is this offer the flight the customer already holds?"
 *
 * Change Flight marks the customer's booked flight "Current" (and lets a
 * round-trip customer keep their departure while changing only the return);
 * Reserve Switch marks the reserved flight "Selected". Both need to recognise
 * a booked flight inside fresh search results.
 *
 * A scheduled flight is identified the way airlines identify it: marketing
 * carrier + flight number + origin + destination + local departure DATE.
 * Deliberately NOT by the departure minute, and NOT by raw string format:
 *
 *   - Airlines retime flights routinely between booking and travel. XKB13M
 *     booked DL 1123 at 18:24; by October Delta flew it at 18:39. The old
 *     exact-minute compare stopped recognising her own flight, so Change
 *     Flight never marked it Current and she couldn't tell how to keep her
 *     departure and move only her return.
 *   - Google-sourced offers and bookings carry "YYYY-MM-DD HH:MM"; Duffel
 *     carries "YYYY-MM-DDTHH:MM:SS". Comparing the raw strings could never
 *     match a flight across sources.
 *
 * When either side has no flight number, identity can't be established that
 * way, so we fall back to the departure minute (format-normalised) — the old
 * rule, minus the format bug.
 *
 * `departing_at` is naive airline-local time, so its first ten characters are
 * the local departure date in both formats.
 */

function normalizeIata(val) {
  if (!val) return '';
  if (typeof val === 'string') return val.trim().toUpperCase();
  return String(val.iata_code || val.IATA || '').trim().toUpperCase();
}

function carrierKey(seg) {
  const c = seg?.marketing_carrier;
  return String(c?.iata_code || c?.IATA || c?.name || '').trim().toUpperCase();
}

/**
 * "DL 1123", "DL1123", "1123" and "DL 01123" all → "1123". The carrier prefix
 * is stripped when it is the segment's own carrier code, or any two-character
 * code containing a letter (IATA codes always do, e.g. "B6", "9K") — a purely
 * numeric number like "1123" is never mistaken for a prefix.
 */
export function normalizeFlightNumber(seg) {
  const raw = String(
    seg?.marketing_carrier_flight_number ?? seg?.flight_number ?? seg?.number ?? ''
  ).toUpperCase().replace(/[\s-]/g, '');
  if (!raw) return '';
  let num = raw;
  const carrier = String(seg?.marketing_carrier?.iata_code || '').trim().toUpperCase();
  if (carrier && num.startsWith(carrier) && num.length > carrier.length) {
    num = num.slice(carrier.length);
  } else {
    const m = num.match(/^([A-Z0-9]{2})(\d+[A-Z]?)$/);
    if (m && /[A-Z]/.test(m[1])) num = m[2];
  }
  return num.replace(/^0+(?=\d)/, '');
}

/** Local departure date "YYYY-MM-DD", or '' when absent/unparseable. */
export function departureDate(seg) {
  const s = String(seg?.departing_at || '');
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : '';
}

/** "2026-12-17 18:24" and "2026-12-17T18:24:00" → "2026-12-17T18:24". */
function departureMinute(seg) {
  const m = String(seg?.departing_at || '').match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : '';
}

export function isSameScheduledSegment(a, b) {
  if (!a || !b) return false;
  if (normalizeIata(a.origin) !== normalizeIata(b.origin)) return false;
  if (normalizeIata(a.destination) !== normalizeIata(b.destination)) return false;
  const carrier = carrierKey(a);
  if (!carrier || carrier !== carrierKey(b)) return false;
  const date = departureDate(a);
  if (!date || date !== departureDate(b)) return false;
  const na = normalizeFlightNumber(a);
  const nb = normalizeFlightNumber(b);
  if (na && nb) return na === nb;
  // No flight number on one side: fall back to the departure minute.
  const ma = departureMinute(a);
  return !!ma && ma === departureMinute(b);
}

/** Same scheduled flight(s), segment for segment. */
export function isSameScheduledSlice(a, b) {
  const sa = a?.segments;
  const sb = b?.segments;
  if (!Array.isArray(sa) || !Array.isArray(sb) || !sa.length || sa.length !== sb.length) return false;
  return sa.every((seg, i) => isSameScheduledSegment(seg, sb[i]));
}
